import { Link } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import * as React from 'react';

const imgClassName =
  'w-[38rem] md:w-[44rem] rounded-md shadow-2xl ring-1 ring-gray-900/10';

const contentDefault = {
  preTitle: 'Effortless planning',
  title: 'How it works',
  description: (
    <>
      Quickly set up rooms, add participants, and create seating charts using
      intuitive tools.
    </>
  ),
  steps: {
    '1': {
      title: 'Set up available seats',
      description: [
        <>
          Create the layout of your room. Pick chair and desk styles. Rotate and
          place them freely with familiar features like drag-and-drop and an
          automatic snap guide.
        </>,
      ],
      img: (
        <StaticImage
          className={imgClassName}
          src="../../images/landing/how-it-works/generic-room-04-24.png"
          alt="Room creator screenshot in Seating Chart Maker. A room layout is being created with chairs and tables. A table group with 8 chairs is selected for rotation or moving it."
          width={704}
          loading="lazy"
        />
      ),
    },
    '2': {
      title: 'Create or import a roster',
      description: [
        <>
          Seating Chart Maker lets you quickly populate your roster by copying
          and pasting names from any application or text file or importing data
          from spreadsheets and CSV files.
        </>,
        <>
          In this phase, you can easily set up groups of persons that should be,
          e.g., kept separated, together, or in front of the room in an
          automatically generated chart.
        </>,
      ],
      img: (
        <StaticImage
          className={imgClassName}
          src="../../images/landing/how-it-works/wedding-roster-04-24.png"
          alt="Roster creator screenshot in Seating Chart Maker. A list of names with portrait images is created. Three persons are grouped in a group to indicate they should be seated apart from each other."
          width={704}
          loading="lazy"
        />
      ),
    },
    '3': {
      title: 'Generate a seating chart',
      description: [
        <>
          Generate a random seating chart, tweak it to perfection, and print or
          share it via a link. You can also show it directly from the app in
          full-screen mode.
        </>,
        <>
          You can easily edit existing charts or create new ones with the same
          roster and room.
        </>,
      ],
      img: (
        <StaticImage
          className={imgClassName}
          src="../../images/landing/how-it-works/generic-seating-04-24.png"
          alt="Seating chart generator screenshot in Seating Chart Maker. A seating chart is being created with person cards being dragged to free seats."
          width={704}
          loading="lazy"
        />
      ),
    },
  },
};

type Content = typeof contentDefault;

const contentClassroom: Content = {
  preTitle: 'Effortless plan generator',
  title: 'How it works',
  description: (
    <>
      Set up classroom layout, add students, and create random school seating
      charts in minutes.
    </>
  ),
  steps: {
    '1': {
      title: 'Set up available seats',
      description: [
        <>
          Design your room layout using our intuitive floor plan generator.
          Create the layout of your classroom or select a ready-made template.
          Pick chair and desk styles. Rotate and place them freely with familiar
          features like drag-and-drop, automatic snap guide, and copy-and-paste.
        </>,
      ],
      img: (
        <StaticImage
          className={imgClassName}
          src="../../images/landing/classroom-hiw-room-04-24.png"
          alt="A screenshot of the room creator in Seating Chart Maker. A classroom layout is being created with chairs and table groups. Two table groups are selected for rotation or moving."
          width={704}
          loading="lazy"
        />
      ),
    },
    '2': {
      title: 'Create or import your student roster',
      description: [
        <>
          Seating Chart Maker lets you quickly populate your roster by copying
          and pasting names from any application or text file, importing data
          from spreadsheets and CSV files, and optionally adding student photos.
        </>,
        <>
          In this phase, you can easily set up groups of students that should
          be, for example, kept separated, placed together, or seated at the
          front of the classroom in a randomly generated chart.
        </>,
      ],
      img: (
        <StaticImage
          className={imgClassName}
          src="../../images/landing/classroom-hiw-roster-04-24.png"
          alt="Roster creator screenshot in Seating Chart Maker. A list of student names is created with some names having an attached portrait. There are two sub-groups formed: three students are assigned to be seated in front of the classroom and two students are grouped to be seated next to each other."
          width={704}
          loading="lazy"
        />
      ),
    },
    '3': {
      title: 'Generate a random seating chart',
      description: [
        <>
          Let the seating chart generator randomize your seating plan, or build
          one manually by dragging and dropping student names. Print or share
          via a link. You can also show a seating chart directly from the app in
          full-screen mode.
        </>,
        <>
          Edit existing seating plans or create new ones with the same roster
          and room.
        </>,
      ],
      img: (
        <StaticImage
          className={imgClassName}
          src="../../images/landing/classroom-hiw-chart-04-24.png"
          alt="Seating chart generator screenshot in Seating Chart Maker. A seating plan is being created with student cards being dragged to free seats. 8 out of 26 students currently have an assigned seat."
          width={704}
          loading="lazy"
        />
      ),
    },
  },
};

const contentWeddings: Content = {
  preTitle: 'Effortless planning software',
  title: 'How it works',
  description: (
    <>
      Easily set up your venue layout, manage your guest list, and arrange
      seating with software designed for weddings and events.
    </>
  ),
  steps: {
    '1': {
      title: 'Design your venue layout',
      description: [
        <>
          Create the layout of your venue. Pick table and chair styles, and add
          other objects like a dance floor or a stage. Rotate and place objects
          freely with familiar features like copy-and-paste, drag-and-drop and
          an automatic snap guide.
        </>,
      ],
      img: (
        <StaticImage
          className={imgClassName}
          src="../../images/landing/how-it-works/wedding-room-04-24.png"
          alt="Room creator screenshot in Seating Chart Maker. A small wedding or event venue layout is being created with round tables and chairs. A table group with 6 chairs is selected for rotation or moving it. The room has decorative plants and a candy buffet table."
          width={704}
          loading="lazy"
        />
      ),
    },
    '2': {
      title: 'Create or import a guest list',
      description: [
        <>
          Import guests from spreadsheets or other documents. You can directly
          import all common spreadsheet formats or use our simple
          copy-and-paste-import.
        </>,
        <>
          Categorize guests into groups to keep families and friends together,
          known adversaries apart, and VIPs in the front seats.
        </>,
      ],
      img: (
        <StaticImage
          className={imgClassName}
          src="../../images/landing/how-it-works/wedding-roster-04-24.png"
          alt="Roster creator screenshot in Seating Chart Maker. A list of guest names with portrait images is created. An avatar with initials is used where a portrait is missing. Three guests are grouped to indicate they should be seated apart from each other."
          width={704}
          loading="lazy"
        />
      ),
    },
    '3': {
      title: 'Generate a seating chart',
      description: [
        <>
          Place guests at tables with a{' '}
          <Link
            to={'/#random'}
            className="text-indigo-700 hover:text-indigo-900"
          >
            random
          </Link>{' '}
          suggestions that respect your groupings. Adjust placements as needed
          by dragging and dropping names, or do the seating entirely by hand.
        </>,
        <>
          You can easily create multiple versions and edit existing charts.
          Finalize the setup and share your plan via a link or export to an
          image or PDF format.
        </>,
      ],
      img: (
        <StaticImage
          className={imgClassName}
          src="../../images/landing/how-it-works/wedding-seating-04-24.png"
          alt="Seating chart generator screenshot in Seating Chart Maker. A seating chart is being created with guest cards being dragged to free seats. There is a random-button to generate a new random seating plan. The room has round tables with 16 out of 26 guests seated at them."
          width={704}
          loading="lazy"
        />
      ),
    },
  },
};

const HowItWorks: React.FC<{ content: Content }> = ({ content }) => {
  return (
    <div className="relative isolate overflow-hidden bg-white">
      <svg
        className="absolute inset-0 -z-10 h-full w-full stroke-gray-200 [mask-image:radial-gradient(100%_100%_at_top_right,white,transparent)]"
        aria-hidden="true"
      >
        <defs>
          <pattern
            id="0787a7c5-978c-4f66-83c7-11c213f99cb7"
            width={200}
            height={200}
            x="50%"
            y={-1}
            patternUnits="userSpaceOnUse"
          >
            <path d="M.5 200V.5H200" fill="none" />
          </pattern>
        </defs>
        <rect
          width="100%"
          height="100%"
          strokeWidth={0}
          fill="url(#0787a7c5-978c-4f66-83c7-11c213f99cb7)"
        />
      </svg>

      <div className="flex justify-center pt-10 lg:pt-24 px-6">
        <div className="max-w-2xl">
          <p className="text-base font-semibold leading-7 text-indigo-600">
            {content.preTitle}
          </p>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            {content.title}
          </h2>
          <p className="mt-4 text-lg text-gray-700">{content.description}</p>
        </div>
      </div>

      {/* Step 1. */}
      <div className="mx-auto max-w-7xl px-6 pb-8 pt-14 lg:flex lg:flex-row-reverse lg:px-8 lg:py-20">
        <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-xl lg:flex-shrink-0 lg:pt-28">
          <span className="inline-flex items-center px-3 py-0.5 rounded-full text-lg font-medium bg-indigo-100 text-indigo-800">
            Step 1.
          </span>
          <div className="mt-6">
            <h3 className="text-3xl font-extrabold tracking-tight text-gray-900">
              {content.steps['1'].title}
            </h3>
            {content.steps['1'].description.map((p, i) => (
              <p key={i} className="mt-4 text-lg text-gray-700">
                {p}
              </p>
            ))}
          </div>
        </div>
        <div className="mx-auto mt-16 flex max-w-2xl sm:mt-10 lg:mr-20 lg:ml-0 lg:mt-0 lg:max-w-none lg:flex-none xl:mr-32">
          <div className="max-w-3xl flex-none sm:max-w-5xl lg:max-w-none">
            <div className="-m-2 rounded-xl bg-gray-900/5 p-2 ring-1 ring-inset ring-gray-900/10 lg:-m-4 lg:rounded-2xl lg:p-4">
              {content.steps['1'].img}
            </div>
          </div>
        </div>
      </div>

      {/* Step 2. */}
      <div className="mx-auto max-w-7xl px-6 pb-8 pt-10 lg:flex lg:px-8 lg:py-10">
        <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-xl lg:flex-shrink-0 lg:pt-20">
          <span className="inline-flex items-center px-3 py-0.5 rounded-full text-lg font-medium bg-pink-100 text-pink-800">
            Step 2.
          </span>
          <div className="mt-6">
            <h3 className="text-3xl font-extrabold tracking-tight text-gray-900">
              {content.steps['2'].title}
            </h3>
            {content.steps['2'].description.map((p, i) => (
              <p key={i} className="mt-4 text-lg text-gray-700">
                {p}
              </p>
            ))}
          </div>
        </div>
        <div className="mx-auto mt-16 flex max-w-2xl sm:mt-10 lg:ml-10 lg:mr-0 lg:mt-0 lg:max-w-none lg:flex-none xl:ml-32">
          <div className="max-w-3xl flex-none sm:max-w-5xl lg:max-w-none">
            <div className="-m-2 rounded-xl bg-gray-900/5 p-2 ring-1 ring-inset ring-gray-900/10 lg:-m-4 lg:rounded-2xl lg:p-4">
              {content.steps['2'].img}
            </div>
          </div>
        </div>
      </div>

      {/* Step 3. */}
      <div className="mx-auto max-w-7xl px-6 pb-24 pt-10 sm:pb-32 lg:flex lg:flex-row-reverse lg:px-8">
        <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-xl lg:flex-shrink-0 lg:pt-20">
          <span
            id="step3"
            className="inline-flex items-center px-3 py-0.5 rounded-full text-lg font-medium bg-yellow-100 text-yellow-800"
          >
            Step 3.
          </span>
          <div className="mt-6">
            <h3 className="text-3xl font-extrabold tracking-tight text-gray-900">
              {content.steps['3'].title}
            </h3>
            {content.steps['3'].description.map((p, i) => (
              <p key={i} className="mt-4 text-lg text-gray-700">
                {p}
              </p>
            ))}
          </div>
        </div>
        <div className="mx-auto mt-16 flex max-w-2xl sm:mt-10 lg:mr-20 lg:ml-0 lg:mt-0 lg:max-w-none lg:flex-none xl:mr-32">
          <div className="max-w-3xl flex-none sm:max-w-5xl lg:max-w-none">
            <div className="-m-2 rounded-xl bg-gray-900/5 p-2 ring-1 ring-inset ring-gray-900/10 lg:-m-4 lg:rounded-2xl lg:p-4">
              {content.steps['3'].img}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const HowItWorksDefault = () => <HowItWorks content={contentDefault} />;
export const HowItWorksClassroom = () => (
  <HowItWorks content={contentClassroom} />
);
export const HowItWorksWedding = () => <HowItWorks content={contentWeddings} />;
